import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../features/user-dashboard/auth/AuthContext';
import { catalogPrimaryEndpointForKey, catalogUiForKey } from '../config/analysisLayersCatalog';
import {
  ADMIN_ROLE_LAYER_UI_GROUP_HINT,
  ADMIN_ROLE_LAYER_UI_GROUP_LABEL,
  ADMIN_ROLE_LAYER_UI_GROUP_ORDER,
  adminRoleLayerUiGroupForKey,
  sortRoleLayerRowsForGroup,
  type AdminRoleLayerUiGroupId,
} from '../config/adminRoleLayerGroups';
import {
  LAYER_TAG_LABELS,
  LAYER_TAGS,
  PLANTATION_SUB_ROLE_OPTIONS,
  SIGNUP_ROLE_OPTIONS,
  collapseLayerTagsForSave,
  expandLayerTagsForEditor,
  toggleLayerTag,
  whoSeesLayerTags,
  type LayerTag,
} from '../config/audience';
import adminService, { type RoleLayerEditorRow } from '../services/adminService';
import type { User } from '../types/auth';

function groupLayersByAppUi(layers: RoleLayerEditorRow[]): {
  groupId: AdminRoleLayerUiGroupId;
  label: string;
  rows: RoleLayerEditorRow[];
}[] {
  const buckets = new Map<AdminRoleLayerUiGroupId, RoleLayerEditorRow[]>();
  for (const id of ADMIN_ROLE_LAYER_UI_GROUP_ORDER) {
    buckets.set(id, []);
  }
  for (const layer of layers) {
    const g = adminRoleLayerUiGroupForKey(layer.key);
    buckets.get(g)!.push(layer);
  }
  return ADMIN_ROLE_LAYER_UI_GROUP_ORDER.filter((id) => (buckets.get(id) ?? []).length > 0).map(
    (groupId) => ({
      groupId,
      label: ADMIN_ROLE_LAYER_UI_GROUP_LABEL[groupId],
      rows: sortRoleLayerRowsForGroup(groupId, buckets.get(groupId) ?? []),
    })
  );
}

function isAdminUser(u: User | null): boolean {
  if (!u) return false;
  return u.is_superuser === true || u.role === 'admin';
}

function resolvedUi(layer: RoleLayerEditorRow): string {
  const fromDb = layer.ui?.trim();
  if (fromDb) return fromDb;
  return catalogUiForKey(layer.key) ?? '';
}

function resolvedEndpoint(layer: { key: string; endpoint: string | null }): {
  text: string;
  source: 'db' | 'catalog';
} {
  const fromDb = layer.endpoint?.trim();
  if (fromDb) return { text: fromDb, source: 'db' };
  const fromCatalog = catalogPrimaryEndpointForKey(layer.key);
  if (fromCatalog) return { text: fromCatalog, source: 'catalog' };
  return { text: '', source: 'catalog' };
}

export default function AdminRoleLayersPage() {
  const navigate = useNavigate();
  const { user: me, loading: authLoading } = useAuth();
  const [layers, setLayers] = useState<RoleLayerEditorRow[]>([]);
  const [draft, setDraft] = useState<Record<string, string[]>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [previewRole, setPreviewRole] = useState('blo');
  const [previewSub, setPreviewSub] = useState('banana');
  const [newKey, setNewKey] = useState('');
  const [newName, setNewName] = useState('');
  const [newTags, setNewTags] = useState<string[]>([...LAYER_TAGS]);
  const [creating, setCreating] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [syncNote, setSyncNote] = useState('');

  const layerGroups = useMemo(() => groupLayersByAppUi(layers), [layers]);
  const selected = layers.find((l) => l.id === selectedId) ?? null;
  const selectedTags = selected ? draft[selected.id] ?? selected.audience_tags ?? [] : [];

  const loadAll = useCallback(async () => {
    setErr('');
    setLoading(true);
    try {
      const data = await adminService.listLayerTags();
      setLayers(data);
      const next: Record<string, string[]> = {};
      for (const row of data) next[row.id] = expandLayerTagsForEditor(row.audience_tags);
      setDraft(next);
      setSelectedId((cur) => {
        if (cur && data.some((d) => d.id === cur)) return cur;
        return data[0]?.id ?? null;
      });
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to load';
      setErr(String(msg));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!isAdminUser(me)) return;
    void loadAll();
  }, [authLoading, me, loadAll]);

  useEffect(() => {
    if (authLoading) return;
    if (me && !isAdminUser(me)) {
      navigate('/', { replace: true });
    }
  }, [authLoading, me, navigate]);

  const toggleTag = (layerId: string, tag: string) => {
    setDraft((d) => ({ ...d, [layerId]: toggleLayerTag(d[layerId], tag) }));
  };

  const groupTagState = (rows: RoleLayerEditorRow[], tag: string): 'all' | 'some' | 'none' => {
    let checked = 0;
    for (const row of rows) {
      if ((draft[row.id] ?? []).includes(tag)) checked += 1;
    }
    if (checked === 0) return 'none';
    if (checked === rows.length) return 'all';
    return 'some';
  };

  const toggleGroupTag = (rows: RoleLayerEditorRow[], tag: string) => {
    const turnOn = groupTagState(rows, tag) !== 'all';
    setDraft((d) => {
      const next = { ...d };
      for (const row of rows) {
        const current = next[row.id] ?? [];
        const has = current.includes(tag);
        if (turnOn && !has) next[row.id] = toggleLayerTag(current, tag);
        if (!turnOn && has) next[row.id] = toggleLayerTag(current, tag);
      }
      return next;
    });
  };

  const saveAll = async () => {
    setSaving(true);
    setErr('');
    try {
      for (const layer of layers) {
        const tags = collapseLayerTagsForSave(draft[layer.id]);
        const prev = [...collapseLayerTagsForSave(layer.audience_tags || [])].sort().join(',');
        const next = [...tags].sort().join(',');
        if (prev !== next) {
          await adminService.putLayerTags(layer.id, tags);
        }
      }
      await loadAll();
    } catch (e: unknown) {
      const msg =
        (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Failed to save';
      setErr(String(msg));
    } finally {
      setSaving(false);
    }
  };

  const createLayer = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newKey.trim() || !newName.trim()) {
      setErr('New layer needs a key and name.');
      return;
    }
    setCreating(true);
    setErr('');
    try {
      await adminService.createAnalysisLayer({
        key: newKey.trim(),
        name: newName.trim(),
        audience_tags: collapseLayerTagsForSave(newTags.length ? newTags : ['all']),
      });
      setNewKey('');
      setNewName('');
      setNewTags([...LAYER_TAGS]);
      await loadAll();
    } catch (ex: unknown) {
      const msg =
        (ex as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Could not create layer';
      setErr(String(msg));
    } finally {
      setCreating(false);
    }
  };

  const syncCatalog = async () => {
    if (syncing) return;
    if (
      !window.confirm(
        'Sync analysis layers from the latest WebGIS catalog? Existing audience tags are kept; new rows (e.g. coconut) are added.'
      )
    ) {
      return;
    }
    setSyncing(true);
    setErr('');
    setSyncNote('');
    try {
      const result = await adminService.syncAnalysisLayersCatalog();
      const created = result.created?.length ? ` Created: ${result.created.join(', ')}.` : '';
      setSyncNote(
        `Synced ${result.catalog_count} catalog layers.${created}${
          result.notes?.length ? ` ${result.notes.join(' ')}` : ''
        }`
      );
      await loadAll();
    } catch (ex: unknown) {
      const msg =
        (ex as { response?: { data?: { detail?: string } } })?.response?.data?.detail ||
        'Catalog sync failed';
      setErr(String(msg));
    } finally {
      setSyncing(false);
    }
  };

  const previewKeys = useMemo(() => {
    const sub = previewRole === 'plantation_owner' ? previewSub : null;
    return layers.filter((layer) => {
      const tags = draft[layer.id] ?? layer.audience_tags ?? [];
      const who = whoSeesLayerTags(tags);
      const needle =
        previewRole === 'plantation_owner'
          ? previewSub === 'coconut'
            ? 'coconut'
            : 'banana'
          : previewRole === 'project_developer'
            ? 'Project developer'
            : previewRole === 'lgu'
              ? 'LGU'
              : 'BLO';
      return who.some((label) => label.toLowerCase().includes(needle.toLowerCase()));
    });
  }, [draft, layers, previewRole, previewSub]);

  if (authLoading || (me && !isAdminUser(me))) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="animate-spin rounded-full h-10 w-10 border-2 border-emerald-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <AdminLayout title="Layer audience tags">
      <div className="max-w-7xl w-full">
        <p className="text-sm text-gray-600 mb-3">
          Tag each analysis tool for the customers who should see it. Click a role checkbox at the
          top of a group to enable or disable that role for every layer in the group.{' '}
          <strong>All</strong> reaches every customer role. Crop tags (<strong>banana</strong> /{' '}
          <strong>coconut</strong>) are for plantation owners; <strong>BLO</strong>, <strong>LGU</strong>,
          and <strong>carbon</strong> match those mains. Click <strong>Save tags</strong> to apply.
        </p>
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => void syncCatalog()}
            disabled={syncing || loading}
            className="rounded-lg bg-emerald-700 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-800 disabled:opacity-50"
          >
            {syncing ? 'Syncing catalog…' : 'Sync latest catalog to database'}
          </button>
          <span className="text-[11px] text-slate-500">
            Runs the current WebGIS layer upsert (adds coconut and other new catalog rows).
          </span>
        </div>
        {syncNote && (
          <div className="mb-3 p-3 rounded-lg bg-emerald-50 text-emerald-950 border border-emerald-200 text-sm">
            {syncNote}
          </div>
        )}
        {err && (
          <div className="mb-3 p-3 rounded-lg bg-amber-50 text-amber-900 border text-sm">{err}</div>
        )}

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_280px]">
          <div className="rounded-xl border border-gray-200 shadow overflow-hidden bg-gray-50/80">
            {loading ? (
              <p className="p-6 text-gray-500">Loading…</p>
            ) : (
              <>
                <div className="p-3 sm:p-4 space-y-4">
                  {layerGroups.map((g, cardIdx) => (
                    <section
                      key={g.groupId}
                      className="rounded-xl border border-slate-200 bg-white shadow-md shadow-slate-900/5 ring-1 ring-slate-900/[0.06] overflow-hidden border-l-[5px] border-l-emerald-500"
                    >
                      <div className="px-3 sm:px-4 py-2.5 sm:py-3 border-b border-slate-100 bg-gradient-to-r from-emerald-50/90 via-white to-slate-50/40">
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-lg bg-emerald-600 text-[11px] font-bold text-white shadow-sm">
                            {cardIdx + 1}
                          </span>
                          <div className="min-w-0 flex-1">
                            <h3 className="text-sm font-semibold text-slate-800 tracking-tight">{g.label}</h3>
                            {ADMIN_ROLE_LAYER_UI_GROUP_HINT[g.groupId] ? (
                              <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                                {ADMIN_ROLE_LAYER_UI_GROUP_HINT[g.groupId]}
                              </p>
                            ) : null}
                          </div>
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="min-w-full text-sm border-collapse">
                          <thead>
                            <tr className="bg-slate-50/95 text-left border-b border-slate-100">
                              <th className="sticky left-0 z-10 bg-slate-50 border-r border-slate-100 p-2 font-medium min-w-[200px]">
                                Tool / layer
                              </th>
                              {LAYER_TAGS.map((tag) => {
                                const state = groupTagState(g.rows, tag);
                                return (
                                  <th
                                    key={tag}
                                    className="p-2 font-medium text-center min-w-[88px] text-slate-700"
                                    title={`Toggle ${LAYER_TAG_LABELS[tag]} for every layer in ${g.label}`}
                                  >
                                    <label className="inline-flex cursor-pointer flex-col items-center gap-1">
                                      <input
                                        type="checkbox"
                                        className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                                        checked={state === 'all'}
                                        ref={(el) => {
                                          if (el) el.indeterminate = state === 'some';
                                        }}
                                        onChange={() => toggleGroupTag(g.rows, tag)}
                                      />
                                      <span>{tag}</span>
                                    </label>
                                  </th>
                                );
                              })}
                            </tr>
                          </thead>
                          <tbody>
                            {g.rows.map((layer, idx) => {
                              const ep = resolvedEndpoint(layer);
                              const uiLine = resolvedUi(layer);
                              const stripe = idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/35';
                              const on = selectedId === layer.id;
                              return (
                                <tr
                                  key={layer.id}
                                  className={`border-b border-slate-100/90 last:border-b-0 hover:bg-emerald-50/35 ${stripe} ${
                                    on ? 'ring-1 ring-inset ring-emerald-400' : ''
                                  }`}
                                  onClick={() => setSelectedId(layer.id)}
                                >
                                  <th
                                    scope="row"
                                    className={`sticky left-0 z-[1] border-r border-slate-100 p-2 text-left font-normal align-middle ${stripe}`}
                                  >
                                    <span className="font-medium text-gray-900">{layer.name}</span>
                                    <code className="ml-1 text-[11px] text-gray-500 font-normal">({layer.key})</code>
                                    {layer.description && (
                                      <span className="block text-xs text-gray-600 mt-1 leading-snug">
                                        {layer.description}
                                      </span>
                                    )}
                                    {uiLine ? (
                                      <div className="text-xs text-gray-700 mt-1.5 leading-snug">
                                        <span className="text-gray-500 font-medium">Where in app: </span>
                                        {uiLine}
                                      </div>
                                    ) : null}
                                    {ep.text ? (
                                      <code className="mt-1 block text-[11px] leading-tight break-all text-violet-900 bg-violet-50 rounded px-1.5 py-1 border border-violet-100">
                                        {ep.text}
                                      </code>
                                    ) : null}
                                  </th>
                                  {LAYER_TAGS.map((tag) => {
                                    const checked = (draft[layer.id] ?? []).includes(tag);
                                    return (
                                      <td key={`${layer.id}-${tag}`} className="p-1 text-center align-middle">
                                        <label className="inline-flex items-center justify-center w-full min-h-[40px] cursor-pointer">
                                          <input
                                            type="checkbox"
                                            className="h-4 w-4 rounded border-gray-300 text-emerald-600"
                                            checked={checked}
                                            onChange={() => toggleTag(layer.id, tag)}
                                            onClick={(ev) => ev.stopPropagation()}
                                          />
                                          <span className="sr-only">
                                            {LAYER_TAG_LABELS[tag as LayerTag]}: {layer.name}
                                          </span>
                                        </label>
                                      </td>
                                    );
                                  })}
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </section>
                  ))}
                </div>
                {layers.length === 0 && <p className="p-4 text-gray-500">No active analysis layers in the catalog.</p>}
                <div className="p-3 border-t border-gray-200 flex flex-wrap items-center justify-end gap-2 bg-gray-50">
                  <button
                    type="button"
                    disabled={saving || layers.length === 0}
                    onClick={() => void saveAll()}
                    className="px-4 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {saving ? 'Saving…' : 'Save tags'}
                  </button>
                </div>
              </>
            )}
          </div>

          <aside className="space-y-3">
            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">Who sees this</h3>
              {selected ? (
                <>
                  <p className="mt-1 text-xs text-slate-600">
                    <span className="font-medium text-slate-800">{selected.name}</span>
                  </p>
                  <ul className="mt-2 space-y-1 text-xs text-slate-700">
                    {whoSeesLayerTags(selectedTags).length ? (
                      whoSeesLayerTags(selectedTags).map((label) => (
                        <li key={label} className="rounded bg-emerald-50 px-2 py-1 text-emerald-900">
                          {label}
                        </li>
                      ))
                    ) : (
                      <li className="text-amber-800">Nobody yet — add at least one tag.</li>
                    )}
                  </ul>
                </>
              ) : (
                <p className="mt-2 text-xs text-slate-500">Select a layer row to preview.</p>
              )}
            </div>

            <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
              <h3 className="text-sm font-semibold text-slate-800">What this role sees</h3>
              <select
                className="mt-2 w-full rounded-md border px-2 py-1.5 text-sm"
                value={previewRole}
                onChange={(e) => setPreviewRole(e.target.value)}
              >
                {SIGNUP_ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              {previewRole === 'plantation_owner' ? (
                <select
                  className="mt-2 w-full rounded-md border px-2 py-1.5 text-sm"
                  value={previewSub}
                  onChange={(e) => setPreviewSub(e.target.value)}
                >
                  {PLANTATION_SUB_ROLE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : null}
              <p className="mt-2 text-[11px] text-slate-500">
                {previewKeys.length} tool{previewKeys.length === 1 ? '' : 's'} with the current draft tags.
              </p>
              <ul className="mt-2 max-h-48 overflow-y-auto space-y-0.5 text-xs text-slate-700">
                {previewKeys.map((l) => (
                  <li key={l.id}>{l.name}</li>
                ))}
              </ul>
            </div>

            <form onSubmit={(e) => void createLayer(e)} className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm space-y-2">
              <h3 className="text-sm font-semibold text-slate-800">Add layer</h3>
              <p className="text-[11px] text-slate-500">New layers are tagged on create (default: all).</p>
              <input
                className="w-full rounded-md border px-2 py-1.5 text-sm"
                placeholder="key (e.g. myTool)"
                value={newKey}
                onChange={(e) => setNewKey(e.target.value)}
              />
              <input
                className="w-full rounded-md border px-2 py-1.5 text-sm"
                placeholder="Display name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
              />
              <div className="flex flex-wrap gap-1.5">
                {LAYER_TAGS.map((tag) => (
                  <label key={tag} className="inline-flex items-center gap-1 text-[11px] text-slate-700">
                    <input
                      type="checkbox"
                      checked={newTags.includes(tag)}
                      onChange={() => setNewTags((cur) => toggleLayerTag(cur, tag))}
                    />
                    {tag}
                  </label>
                ))}
              </div>
              <button
                type="submit"
                disabled={creating}
                className="w-full rounded-lg bg-slate-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-900 disabled:opacity-50"
              >
                {creating ? 'Creating…' : 'Create layer'}
              </button>
            </form>
          </aside>
        </div>
      </div>
    </AdminLayout>
  );
}
